import { Component, NgZone, OnDestroy } from '@angular/core';
import { AppGlobals } from '@app/app.global';
import { AuthService, EntityService, SharedService } from '@app/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PmDemoModeService } from '../../services/pm-demo-mode.service';
import { PmConnectFormCityOption, PmConnectLeadPayload, PriceDiscoveryService } from '../../services/price-discovery.service';

const PM_DEMO_RECAPTCHA_SCRIPT_ID = 'pm-demo-recaptcha-enterprise';
const PM_DEMO_RECAPTCHA_ACTION = 'SUBMIT';

@Component({
    selector: 'app-connect-lead-modal',
    templateUrl: './connect-lead-modal.component.html',
    styleUrls: ['./connect-lead-modal.component.scss']
})
export class ConnectLeadModalComponent implements OnDestroy {
    isConnectLeadPopupOpen = false;
    isConnectLeadSubmitting = false;
    isConnectLeadCitiesLoading = false;
    connectLeadCityOptions: PmConnectFormCityOption[] = [];
    filteredConnectLeadCities: PmConnectFormCityOption[] = [];
    showConnectLeadCityDropdown = false;
    connectLeadCityQuery = '';
    connectLeadForm = {
        full_name: '',
        phone_number: '',
        email: '',
        city: '',
        business_name: ''
    };
    connectLeadErrors: Partial<Record<keyof PmConnectLeadPayload, string>> = {};

    private destroy$ = new Subject<void>();
    private recaptchaScriptPromise: Promise<void> | null = null;

    constructor(
        private authService: AuthService,
        private entityService: EntityService,
        private priceDiscoveryService: PriceDiscoveryService,
        private sharedService: SharedService,
        private pmDemoMode: PmDemoModeService,
        private appGlobals: AppGlobals,
        private ngZone: NgZone
    ) { }

    get isPublicDemo(): boolean {
        return this.pmDemoMode.isActive;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        document.body.style.overflow = '';
    }

    open(): void {
        const user = this.authService.user;
        const entity = this.entityService.getSingleSelectedEntity();
        this.connectLeadErrors = {};
        this.connectLeadForm = {
            full_name: user?.name || '',
            phone_number: this.formatConnectLeadPhone(user?.mobile || ''),
            email: user?.email || '',
            city: '',
            business_name: entity?.name || user?.company?.name || ''
        };
        this.connectLeadCityQuery = '';
        this.showConnectLeadCityDropdown = false;
        this.isConnectLeadPopupOpen = true;
        document.body.style.overflow = 'hidden';
        this.loadConnectLeadCities();
        if (this.isPublicDemo) {
            this.ensureRecaptchaScript();
        }
    }

    closeConnectLeadPopup(): void {
        this.isConnectLeadPopupOpen = false;
        this.showConnectLeadCityDropdown = false;
        this.isConnectLeadSubmitting = false;
        document.body.style.overflow = '';
    }

    onConnectLeadCityQueryChange(query: string): void {
        this.connectLeadCityQuery = query;
        this.connectLeadForm.city = '';
        this.showConnectLeadCityDropdown = true;
        const normalized = (query || '').trim().toLowerCase();
        const matched = !normalized
            ? this.connectLeadCityOptions
            : this.connectLeadCityOptions.filter(option =>
                option.label.toLowerCase().includes(normalized) ||
                option.value.toLowerCase().includes(normalized)
            );
        this.filteredConnectLeadCities = matched.slice(0, 50);
    }

    selectConnectLeadCity(option: PmConnectFormCityOption): void {
        this.connectLeadForm.city = option.value;
        this.connectLeadCityQuery = option.label;
        this.showConnectLeadCityDropdown = false;
        this.connectLeadErrors.city = undefined;
    }

    submitConnectLeadForm(): void {
        if (this.isConnectLeadSubmitting) {
            return;
        }
        if (!this.validateConnectLeadForm()) {
            return;
        }

        this.isConnectLeadSubmitting = true;
        const submit = (recaptchaToken?: string) => {
            const payload: PmConnectLeadPayload = {
                full_name: this.connectLeadForm.full_name.trim(),
                phone_number: this.normalizeConnectLeadPhone(this.connectLeadForm.phone_number),
                email: this.connectLeadForm.email.trim(),
                city: this.connectLeadForm.city.trim(),
                business_name: this.connectLeadForm.business_name.trim()
            };
            if (recaptchaToken) {
                payload['captchaToken'] = recaptchaToken;
            }
            this.priceDiscoveryService.submitPmConnectLead(payload)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: () => {
                        this.isConnectLeadSubmitting = false;
                        this.closeConnectLeadPopup();
                        this.sharedService.successAlert('Thanks! Our team will help you connect with suppliers shortly.');
                    },
                    error: (error) => {
                        this.isConnectLeadSubmitting = false;
                        const message = error || 'Failed to submit details. Please try again.';
                        this.sharedService.errorAlert(message);
                    }
                });
        };

        if (!this.isPublicDemo) {
            submit();
            return;
        }

        this.executeDemoRecaptcha()
            .then(token => this.ngZone.run(() => submit(token)))
            .catch(() => this.ngZone.run(() => {
                this.isConnectLeadSubmitting = false;
                this.sharedService.errorAlert('Captcha verification failed. Please try again.');
            }));
    }

    private ensureRecaptchaScript(): Promise<void> {
        if (this.getEnterpriseRecaptcha()) {
            return Promise.resolve();
        }
        if (this.recaptchaScriptPromise) {
            return this.recaptchaScriptPromise;
        }

        this.recaptchaScriptPromise = new Promise<void>((resolve, reject) => {
            const existing = document.getElementById(PM_DEMO_RECAPTCHA_SCRIPT_ID) as HTMLScriptElement | null;
            if (existing) {
                if (this.getEnterpriseRecaptcha()) {
                    resolve();
                    return;
                }
                existing.addEventListener('load', () => resolve(), { once: true });
                existing.addEventListener('error', () => reject(new Error('Failed to load reCAPTCHA')), { once: true });
                return;
            }

            const script = document.createElement('script');
            script.id = PM_DEMO_RECAPTCHA_SCRIPT_ID;
            script.src = `https://www.google.com/recaptcha/enterprise.js?render=${this.appGlobals.PM_DEMO_RECAPTCHA_SITEKEY}`;
            script.async = true;
            script.defer = true;
            script.onload = () => resolve();
            script.onerror = () => {
                this.recaptchaScriptPromise = null;
                reject(new Error('Failed to load reCAPTCHA'));
            };
            document.head.appendChild(script);
        });

        return this.recaptchaScriptPromise;
    }

    private executeDemoRecaptcha(): Promise<string> {
        return this.ensureRecaptchaScript().then(() => new Promise<string>((resolve, reject) => {
            const recaptcha = this.getEnterpriseRecaptcha();
            if (!recaptcha) {
                reject(new Error('reCAPTCHA is not available'));
                return;
            }
            recaptcha.ready(() => {
                recaptcha.execute(this.appGlobals.PM_DEMO_RECAPTCHA_SITEKEY, { action: PM_DEMO_RECAPTCHA_ACTION })
                    .then(token => token ? resolve(token) : reject(new Error('Empty reCAPTCHA token')))
                    .catch(reject);
            });
        }));
    }

    private getEnterpriseRecaptcha(): { ready: (cb: () => void) => void; execute: (siteKey: string, options: { action: string }) => Promise<string> } | null {
        return (window as any).grecaptcha?.enterprise || null;
    }

    private loadConnectLeadCities(): void {
        if (this.connectLeadCityOptions.length > 0) {
            this.filteredConnectLeadCities = this.connectLeadCityOptions.slice(0, 50);
            return;
        }

        this.isConnectLeadCitiesLoading = true;
        this.priceDiscoveryService.getPmConnectFormCities()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (cities) => {
                    this.connectLeadCityOptions = cities;
                    this.filteredConnectLeadCities = cities.slice(0, 50);
                    this.isConnectLeadCitiesLoading = false;
                },
                error: () => {
                    this.isConnectLeadCitiesLoading = false;
                    this.sharedService.errorAlert('Failed to load cities. Please try again.');
                }
            });
    }

    private formatConnectLeadPhone(raw: string): string {
        const digits = String(raw || '').replace(/\D/g, '');
        if (digits.length === 10) {
            return `+91 ${digits}`;
        }
        if (digits.length === 12 && digits.startsWith('91')) {
            return `+91 ${digits.slice(2)}`;
        }
        return raw || '';
    }

    private normalizeConnectLeadPhone(raw: string): string {
        const digits = String(raw || '').replace(/\D/g, '');
        if (digits.length === 10) {
            return `+91 ${digits}`;
        }
        if (digits.length === 12 && digits.startsWith('91')) {
            return `+91 ${digits.slice(2)}`;
        }
        return String(raw || '').trim();
    }

    private validateConnectLeadForm(): boolean {
        const errors: Partial<Record<keyof PmConnectLeadPayload, string>> = {};
        const fullName = this.connectLeadForm.full_name.trim();
        const email = this.connectLeadForm.email.trim();
        const city = this.connectLeadForm.city.trim();
        const businessName = this.connectLeadForm.business_name.trim();
        const phone = this.normalizeConnectLeadPhone(this.connectLeadForm.phone_number);
        const phoneDigits = phone.replace(/\D/g, '');

        if (!fullName) {
            errors.full_name = 'Full name is required';
        }
        if (!(phoneDigits.length === 12 && phoneDigits.startsWith('91'))) {
            errors.phone_number = 'Enter a valid 10-digit phone number';
        }
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            errors.email = 'Enter a valid email';
        }
        if (!city) {
            errors.city = 'Please select a city from the list';
        }
        if (!businessName) {
            errors.business_name = 'Business name is required';
        }

        this.connectLeadErrors = errors;
        return Object.keys(errors).length === 0;
    }
}
